// Other sign-ins that share the person's verified email (#7426 decision 2), and
// the email-proof bind that merges one into this user. Reads
// `GET /v1/me/link-candidates`; asks with `POST /v1/me/link-requests`, which
// MAILS a single-use link to the shared address. The merge itself happens when
// the person opens that link in THIS browser (`GET /auth/link/confirm/:token`,
// a server-rendered page the dashboard never calls) — the SPA only asks, the
// API proves the email and decides (`apps/dashboard/CLAUDE.md` § Boundary).
//
// Contract: the api seat's link-candidates wire (`apps/api`, `me-link-*`). The
// page that renders it is Connected logins (`routes/ConnectedLogins.tsx`), and
// the shell's banner (`components/LinkCandidatesBanner.tsx`) points there.

import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { ApiError, apiFetch } from './api';
import { SESSION_TOKEN } from './session';

export const LINK_CANDIDATES_QUERY_KEY = 'me_link_candidates';

// Every candidate is linkable: the API lists only SSO-only users of an org the person
// already belongs to (one login through that org's SSO, no other membership), so the
// row names that one shared org and nothing about any other tenant.
const LinkCandidateSchema = z.object({
  userId: z.string(),
  login: z.string(),
  /** The caller's OWN verified address the other user verified too. */
  email: z.string(),
  /** Its one login: that org's SSO protocol. */
  provider: z.enum(['oidc', 'saml']),
  /** The shared org's login. */
  org: z.string(),
});
export type LinkCandidate = z.infer<typeof LinkCandidateSchema>;

const LinkCandidatesSchema = z.object({ data: z.array(LinkCandidateSchema) });
/** `GET /v1/me/link-candidates`'s body. */
export type LinkCandidatesPage = z.infer<typeof LinkCandidatesSchema>;

/**
 * The candidates. `enabled` (default admits) is the caller's gate: the route
 * 403s a credential with no human behind it, so the shell passes "this session
 * names a user" and a PAT sends nothing.
 */
export function useLinkCandidates(
  token: string | null = SESSION_TOKEN,
  enabled: () => boolean = () => true,
) {
  return createQuery(() => ({
    queryKey: [LINK_CANDIDATES_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null && enabled(),
    queryFn: async () =>
      (await apiFetch('/v1/me/link-candidates', token, undefined, { schema: LinkCandidatesSchema }))
        .data,
  }));
}

const LinkRequestSentSchema = z.object({ sentTo: z.string(), expiresAt: z.string() });
/** A 202: where the link went and when it dies. */
export type LinkRequestSent = z.infer<typeof LinkRequestSentSchema>;

/** Ask the API to mail a confirmation link for `userId`. Throws `ApiError` on a refusal. */
export function sendLinkRequest(
  userId: string,
  token: string | null = SESSION_TOKEN,
): Promise<LinkRequestSent> {
  return apiFetch(
    '/v1/me/link-requests',
    token,
    { method: 'POST', body: JSON.stringify({ user_id: userId }) },
    { schema: LinkRequestSentSchema },
  );
}

/**
 * A refused send, by the machine `code` the route names. `other` carries no
 * code the page explains, and shows the API's own `detail` (`errorReason`).
 */
export type LinkRequestRefusal =
  | { readonly kind: 'not_a_candidate' }
  /** `per`: whose budget ran out — this person's, or the address's (across requesters). */
  | {
      readonly kind: 'rate_limited';
      readonly per: 'requester' | 'address';
      readonly retryAfterSeconds: number | null;
    }
  | { readonly kind: 'send_failed' }
  | { readonly kind: 'browser_session_required' }
  | { readonly kind: 'other' };

export function linkRequestRefusal(err: unknown): LinkRequestRefusal {
  if (!(err instanceof ApiError)) return { kind: 'other' };
  const code = err.extensions?.['code'];
  if (code === 'not_a_candidate') return { kind: 'not_a_candidate' };
  if (code === 'rate_limited' || err.status === 429) {
    const after = err.extensions?.['retryAfterSeconds'];
    return {
      kind: 'rate_limited',
      per: err.extensions?.['per'] === 'address' ? 'address' : 'requester',
      retryAfterSeconds: typeof after === 'number' && after > 0 ? Math.ceil(after) : null,
    };
  }
  if (code === 'send_failed') return { kind: 'send_failed' };
  if (code === 'browser_session_required') return { kind: 'browser_session_required' };
  return { kind: 'other' };
}

/**
 * The shell banner's dismissal: the candidate ids the person waved away, in
 * this browser (`lib/local-pref.ts` — a per-viewer convenience, not state the
 * server needs). A candidate that appears LATER is not covered by it, so the
 * banner comes back for news and never for a sign-in already seen.
 */
export const LINK_BANNER_PREF = 'dz-link-candidates-dismissed';
export const LinkBannerDismissedSchema = z.array(z.string());

/** The candidates the banner still owes a mention: not dismissed. */
export function bannerCandidates(
  candidates: readonly LinkCandidate[],
  dismissed: readonly string[],
): LinkCandidate[] {
  return candidates.filter((c) => !dismissed.includes(c.userId));
}
